import { Component, OnInit, OnDestroy, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AuthSignalService } from '../../../../core/state/auth.signal';
import { SocketService } from '../../../../core/socket/socket.service';

type Action = 'break' | 'lunch' | 'checkout';

@Component({
  selector: 'app-availability-widget',
  standalone: false,
  templateUrl: './availability-widget.component.html',
})
export class AvailabilityWidgetComponent implements OnInit, OnDestroy {
  visible = false;
  info: any = null;
  remaining = 0; // segundos (cronómetro local)
  loading = false;

  confirm: { open: boolean; action: Action | null; title: string; message: string } = {
    open: false, action: null, title: '', message: '',
  };
  alert: { open: boolean; message: string } = { open: false, message: '' };

  toastMessage: string | null = null;
  toastType: 'success' | 'error' = 'success';

  private timer: any = null;
  private subs = new Subscription();
  private loadedOnce = false;

  constructor(
    private http: HttpClient,
    private authSignal: AuthSignalService,
    private socket: SocketService,
  ) {
    // Reactivo: en cuanto el usuario (con sus teamIds) esté disponible —recién
    // logueado o tras recargar— se decide la visibilidad y se carga el estado.
    // Evita tener que recargar la página para que aparezca el control.
    effect(() => {
      const user = this.authSignal.user();
      const isMember = !!user && Array.isArray(user.teamIds) && user.teamIds.length > 0;
      this.visible = isMember;
      if (isMember && !this.loadedOnce) {
        this.loadedOnce = true;
        this.load();
      }
    });
  }

  ngOnInit(): void {
    // Re-sincroniza ante cambios del servidor (fin de cronómetro, salida automática).
    this.subs.add(this.socket.onAvailabilityUpdate().subscribe(() => this.load()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopTimer();
  }

  private load(): void {
    this.http.get<any>(`${environment.apiUrl}/availability/me`).subscribe({
      next: (res) => {
        this.info = res;
        this.remaining = res.remainingSeconds || 0;
        if (this.remaining > 0 && (res.status === 'BREAK' || res.status === 'LUNCH')) {
          this.startTimer();
        } else {
          this.stopTimer();
        }
      },
      error: () => {},
    });
  }

  private startTimer(): void {
    this.stopTimer();
    this.timer = setInterval(() => {
      this.remaining--;
      if (this.remaining <= 0) {
        this.stopTimer();
        const wasStatus = this.info?.status;
        this.alert = {
          open: true,
          message: wasStatus === 'LUNCH'
            ? 'Tu hora de almuerzo terminó. Vuelves a estar activo.'
            : 'Tu descanso terminó. Vuelves a estar activo.',
        };
        this.load(); // re-sincroniza con el servidor
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  // ── Acciones ──
  ask(action: Action): void {
    const map: Record<Action, { title: string; message: string }> = {
      break: { title: 'Tomar un descanso', message: `¿Iniciar un descanso de ${this.info?.break?.durationMin || 5} minutos? Durante ese tiempo no se te asignarán nuevos chats.` },
      lunch: { title: 'Hora de almuerzo', message: `¿Iniciar tu hora de almuerzo (${this.info?.lunch?.durationMin || 60} min)? No se te asignarán nuevos chats hasta que termine.` },
      checkout: { title: 'Marcar salida', message: '¿Marcar tu salida del día? Dejarás de recibir chats hasta mañana.' },
    };
    this.confirm = { open: true, action, ...map[action] };
  }

  confirmAction(): void {
    const action = this.confirm.action;
    if (!action) return;
    this.loading = true;
    this.http.post<any>(`${environment.apiUrl}/availability/${action}`, {}).subscribe({
      next: (res) => {
        this.loading = false;
        this.confirm.open = false;
        this.info = res;
        this.remaining = res.remainingSeconds || 0;
        if (this.remaining > 0) this.startTimer();
      },
      error: (err) => {
        this.loading = false;
        this.confirm.open = false;
        this.showToast(err?.error?.message || 'No se pudo realizar la acción.', 'error');
      },
    });
  }

  // ── Helpers de presentación ──
  get countdownLabel(): string {
    const m = Math.floor(this.remaining / 60);
    const s = this.remaining % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  get statusLabel(): string {
    switch (this.info?.status) {
      case 'BREAK': return 'En descanso';
      case 'LUNCH': return 'En almuerzo';
      case 'CHECKED_OUT': return 'Jornada finalizada';
      default: return 'Disponible';
    }
  }

  showToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    setTimeout(() => (this.toastMessage = null), 4000);
  }
}
